import React from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboardIcon, PlaneIcon, BriefcaseIcon, CheckCircleIcon, WalletIcon, UsersIcon, BarChart3Icon, SettingsIcon, LogOutIcon, ShieldIcon, Building2Icon, ServerIcon, ReceiptIcon, BellIcon, PlayIcon, ClipboardListIcon, HeadphonesIcon, CalendarIcon, CreditCardIcon, PlugIcon, FileTextIcon, ScrollIcon, XIcon } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
interface SidebarProps {
  onLogout: () => void;
  userRole: string | null;
  isOpen: boolean;
  onClose: () => void;
}
interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  badge?: string;
}
interface NavSection {
  title: string;
  items: NavItem[];
}
export function Sidebar({
  onLogout,
  userRole,
  isOpen,
  onClose
}: SidebarProps) {
  const {
    operationsQueue,
    notifications,
    supportCases
  } = useAppContext();
  const unreadNotifications = notifications.filter(n => !n.read).length;
  const openCases = supportCases.filter(c => c.status === 'open' || c.status === 'in_progress').length;
  const getNavSections = (): NavSection[] => {
    switch (userRole) {
      case 'super_admin':
        return [{
          title: '',
          items: [{
            icon: PlayIcon,
            label: 'Demo Flow',
            path: '/demo'
          }, {
            icon: LayoutDashboardIcon,
            label: 'Platform Overview',
            path: '/super-admin'
          }, {
            icon: Building2Icon,
            label: 'Tenants',
            path: '/super-admin/tenants'
          }, {
            icon: ServerIcon,
            label: 'System Settings',
            path: '/super-admin/settings'
          }, {
            icon: BarChart3Icon,
            label: 'Global Analytics',
            path: '/super-admin/analytics'
          }, {
            icon: BellIcon,
            label: 'Notifications',
            path: '/notifications',
            badge: unreadNotifications > 0 ? String(unreadNotifications) : undefined
          }]
        }];
      case 'tenant_admin':
        return [{
          title: 'MAIN',
          items: [{
            icon: PlayIcon,
            label: 'Demo Flow',
            path: '/demo'
          }, {
            icon: LayoutDashboardIcon,
            label: 'Dashboard',
            path: '/'
          }, {
            icon: BriefcaseIcon,
            label: 'My Trips',
            path: '/trips'
          }, {
            icon: PlaneIcon,
            label: 'Book Travel',
            path: '/booking'
          }]
        }, {
          title: 'OPERATIONS',
          items: [{
            icon: CheckCircleIcon,
            label: 'Approvals',
            path: '/approvals',
            badge: operationsQueue.pendingApprovals > 0 ? String(operationsQueue.pendingApprovals) : undefined
          }, {
            icon: ClipboardListIcon,
            label: 'Operations Queue',
            path: '/operations'
          }, {
            icon: HeadphonesIcon,
            label: 'Support',
            path: '/support',
            badge: openCases > 0 ? String(openCases) : undefined
          }]
        }, {
          title: 'FINANCE',
          items: [{
            icon: WalletIcon,
            label: 'Budget',
            path: '/budget'
          }, {
            icon: ReceiptIcon,
            label: 'Expenses',
            path: '/expenses'
          }, {
            icon: CreditCardIcon,
            label: 'Payments',
            path: '/payments'
          }]
        }, {
          title: 'MARKETING',
          items: [{
            icon: CalendarIcon,
            label: 'Events',
            path: '/events'
          }, {
            icon: BellIcon,
            label: 'Notifications',
            path: '/notifications',
            badge: unreadNotifications > 0 ? String(unreadNotifications) : undefined
          }]
        }, {
          title: 'ADMIN',
          items: [{
            icon: BarChart3Icon,
            label: 'Reports',
            path: '/reports'
          }, {
            icon: ScrollIcon,
            label: 'Audit Logs',
            path: '/audit-logs'
          }, {
            icon: PlugIcon,
            label: 'Integrations',
            path: '/integrations'
          }, {
            icon: FileTextIcon,
            label: 'Refund Policies',
            path: '/refund-policies'
          }, {
            icon: UsersIcon,
            label: 'Travelers',
            path: '/travelers'
          }, {
            icon: UsersIcon,
            label: 'Team Members',
            path: '/admin/users'
          }, {
            icon: SettingsIcon,
            label: 'Settings',
            path: '/settings'
          }]
        }];
      case 'manager':
        return [{
          title: 'MAIN',
          items: [{
            icon: PlayIcon,
            label: 'Demo Flow',
            path: '/demo'
          }, {
            icon: LayoutDashboardIcon,
            label: 'Dashboard',
            path: '/'
          }, {
            icon: PlaneIcon,
            label: 'Book Travel',
            path: '/booking'
          }, {
            icon: BriefcaseIcon,
            label: 'My Trips',
            path: '/trips'
          }]
        }, {
          title: 'OPERATIONS',
          items: [{
            icon: CheckCircleIcon,
            label: 'Approvals',
            path: '/approvals',
            badge: operationsQueue.pendingApprovals > 0 ? String(operationsQueue.pendingApprovals) : undefined
          }, {
            icon: ClipboardListIcon,
            label: 'Operations Queue',
            path: '/operations'
          }, {
            icon: HeadphonesIcon,
            label: 'Support',
            path: '/support'
          }]
        }, {
          title: 'FINANCE',
          items: [{
            icon: ReceiptIcon,
            label: 'Expenses',
            path: '/expenses'
          }, {
            icon: CreditCardIcon,
            label: 'Payments',
            path: '/payments'
          }]
        }, {
          title: 'MORE',
          items: [{
            icon: CalendarIcon,
            label: 'Events',
            path: '/events'
          }, {
            icon: UsersIcon,
            label: 'My Team',
            path: '/travelers'
          }, {
            icon: BarChart3Icon,
            label: 'Reports',
            path: '/reports'
          }, {
            icon: BellIcon,
            label: 'Notifications',
            path: '/notifications',
            badge: unreadNotifications > 0 ? String(unreadNotifications) : undefined
          }]
        }];
      case 'traveler':
      default:
        return [{
          title: 'MAIN',
          items: [{
            icon: PlayIcon,
            label: 'Demo Flow',
            path: '/demo'
          }, {
            icon: LayoutDashboardIcon,
            label: 'Dashboard',
            path: '/'
          }, {
            icon: PlaneIcon,
            label: 'Book Travel',
            path: '/booking'
          }, {
            icon: BriefcaseIcon,
            label: 'My Trips',
            path: '/trips'
          }]
        }, {
          title: 'MORE',
          items: [{
            icon: WalletIcon,
            label: 'My Budget',
            path: '/budget'
          }, {
            icon: CalendarIcon,
            label: 'Events',
            path: '/events'
          }, {
            icon: HeadphonesIcon,
            label: 'Support',
            path: '/support'
          }, {
            icon: BellIcon,
            label: 'Notifications',
            path: '/notifications',
            badge: unreadNotifications > 0 ? String(unreadNotifications) : undefined
          }]
        }];
    }
  };
  const navSections = getNavSections();
  const getRoleLabel = () => {
    switch (userRole) {
      case 'super_admin':
        return {
          label: 'Super Admin',
          color: 'text-gold-400'
        };
      case 'tenant_admin':
        return {
          label: 'Admin',
          color: 'text-gold-400'
        };
      case 'manager':
        return {
          label: 'Manager',
          color: 'text-emerald-400'
        };
      default:
        return {
          label: 'Traveler',
          color: 'text-slate-400'
        };
    }
  };
  const roleInfo = getRoleLabel();
  const handleNavClick = () => {
    onClose();
  };
  return <>
      {/* Mobile backdrop */}
      {isOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} aria-hidden="true" data-id="element-124" />}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 z-50 h-screen w-64 bg-navy-900 text-slate-400 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:z-40 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-125">
        {/* Logo + Mobile Close */}
        <div className="flex h-16 items-center justify-between border-b border-white/10 px-4" data-id="element-126">
          <img src="https://cdn.magicpatterns.com/uploads/76CAA7KBYMWEPu97mZSYz3/Untitled_design.png" alt="Super International Travels Limited" className="h-9 w-auto max-w-[200px] object-contain mr-2 flex-shrink-0" data-id="element-127" />
          <button onClick={onClose} className="lg:hidden rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors" aria-label="Close sidebar" data-id="element-128">
            <XIcon className="h-5 w-5" data-id="element-129" />
          </button>
        </div>

        <div className="flex flex-col justify-between h-[calc(100vh-4rem)]" data-id="element-130">
          <nav className="px-3 py-3 space-y-1 overflow-y-auto custom-scrollbar flex-1" data-id="element-131">
            {navSections.map((section, sIdx) => <div key={sIdx} className={sIdx > 0 ? 'mt-4' : ''} data-id="element-132">
                {section.title && <div className="px-3 mb-2 mt-1" data-id="element-133">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-134">
                      {section.title}
                    </span>
                  </div>}
                {section.items.map(item => <NavLink key={item.path + item.label} to={item.path} onClick={handleNavClick} className={({
              isActive
            }) => `flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-navy-700 text-white' : 'hover:bg-white/5 hover:text-white'}`} data-id="element-135">
                    <item.icon className="mr-3 h-4.5 w-4.5 flex-shrink-0" data-id="element-136" />
                    <span className="flex-1 truncate" data-id="element-137">{item.label}</span>
                    {item.badge && <span className="ml-auto inline-block rounded-full bg-gold-500 px-2 py-0.5 text-[10px] font-bold text-navy-900" data-id="element-138">
                        {item.badge}
                      </span>}
                  </NavLink>)}
              </div>)}
          </nav>

          <div className="border-t border-white/10 p-4" data-id="element-139">
            <div className="flex items-center gap-3 mb-3" data-id="element-140">
              <img className="h-9 w-9 rounded-full bg-slate-700" src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="User avatar" data-id="element-141" />
              <div className="flex-1 min-w-0" data-id="element-142">
                <p className="truncate text-sm font-medium text-white" data-id="element-143">
                  Alex Morgan
                </p>
                <p className={`truncate text-xs ${roleInfo.color}`} data-id="element-144">
                  {roleInfo.label}
                </p>
              </div>
            </div>
            <button onClick={() => {
            onClose();
            onLogout();
          }} className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors" data-id="element-145">
              <LogOutIcon className="mr-3 h-4 w-4" data-id="element-146" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>;
}